import {
  Accordion,
  Box,
  Button,
  ButtonGroup,
  Heading,
  InlineFeedback,
  Modal,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { HighPrioritySystemMessage } from '@zg-rentals/satellite-components';
import { SatelliteConversationMessageType, SatelliteUiTreatmentType } from '@zg-rentals/ts-satellite-client';
import useModal from '../../../hooks/useModal';

const SYSTEM_MESSAGE_TEXT =
  "The landlord has asked you to complete an optional Renter Profile so they can learn more about you. Show you're interested by sharing more information up front.";

type RequestRenterProfileModalProps = {
  name: string;
  onRequestRenterProfileClick: () => void;
  hasError: boolean;
  onClose?: () => void;
};

export default function RequestRenterProfileModal({
  name,
  onRequestRenterProfileClick,
  hasError,
  onClose,
}: RequestRenterProfileModalProps): JSX.Element {
  const { clearModal } = useModal();

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    clearModal();
  };

  const handleRequestClick = () => onRequestRenterProfileClick();

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      size={{
        base: 'fullScreen',
        md: 'md',
      }}
      header={
        <Heading level={2} textStyle="heading-xs">
          Request information from renter
        </Heading>
      }
      body={
        <>
          <Heading level={3} fontFamily="heading-alt" textStyle="heading-sm">
            Ask {name} to complete a Renter Profile?
          </Heading>

          <Paragraph css={{ marginTop: 'default' }}>
            Zillow can ask the renter to complete a Renter Profile, which may include details like: a move-in date,
            self-reported credit score, pets, income range, and more.
          </Paragraph>

          <Paragraph css={{ marginTop: 'default', marginBottom: 'default' }}>
            This is a preview of the message that we'll send to {name}:
          </Paragraph>

          <Box css={{ display: 'flex', justifyContent: 'center', marginBottom: 'looser' }}>
            <HighPrioritySystemMessage
              ctaText="Complete Renter Profile"
              message={SYSTEM_MESSAGE_TEXT}
              messageType={SatelliteConversationMessageType.RENTER_PROFILE_REQUEST}
              onCtaClick={() => null}
              shouldDisplayAsPreview={true}
              messageDateMs=""
              title="Renter Profile requested"
              uiTreatmentType={SatelliteUiTreatmentType.HIGH_PRIORITY}
            />
          </Box>

          <Accordion.Root css={{ marginBottom: 'tighter' }}>
            <Accordion.Item value="renter-profile-info">
              <Accordion.Header>
                <Heading level={4} textStyle="heading-xs">
                  What is a Renter Profile?
                </Heading>
              </Accordion.Header>
              <Accordion.Panel>
                <Paragraph>
                  A Renter Profile is optional for renters to complete and provides you with valuable information about
                  renters before they apply.
                </Paragraph>
                <Paragraph css={{ marginTop: 'default' }}>
                  It can include details such as a move-in date, self-reported credit score, pets, income range, and
                  more.
                </Paragraph>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion.Root>
        </>
      }
      footer={
        <Box css={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <ButtonGroup css={{ width: '100%' }} aria-label="Modal actions">
            <TextButton onClick={handleClose}>Cancel</TextButton>
            <Button onClick={handleRequestClick} emphasis="filled" tone="brand" css={{ whiteSpace: 'nowrap' }}>
              Request
            </Button>
          </ButtonGroup>

          {hasError && (
            <Box css={{ display: 'flex', justifyContent: 'flex-end', flex: '0 0 100%' }}>
              <InlineFeedback tone="critical" css={{ marginTop: 'tighter' }}>
                Sorry, there was an error. Please try again.
              </InlineFeedback>
            </Box>
          )}
        </Box>
      }
    />
  );
}
